import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DateRangeProvider } from "@/context/DateRangeContext";
import Layout from "@/components/Layout";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import AcceptInvitation from "@/pages/AcceptInvitation";
import SalesPerformance from "@/pages/SalesPerformance";
import CreditSales from "@/pages/CreditSales";
import OnlineSales from "@/pages/OnlineSales";
import CashSales from "@/pages/CashSales";
import PettyCash from "@/pages/PettyCash";
import CompanyDashboard from "@/pages/CompanyDashboard";
import BranchMaster from "@/pages/BranchMaster";
import UsersPage from "@/pages/Users";
import AuditLog from "@/pages/AuditLog";
import Attendance from "@/pages/Attendance";
import KpiManagement from "@/pages/KpiManagement";
import StockControl from "@/pages/StockControl";
import PaymentTracking from "@/pages/PaymentTracking";

function Protected({ children, permission }) {
  const { user, can } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (permission && !can(permission)) {
    const firstAllowed = [
      ["page.overview", "/"], ["page.credit_sales", "/sales/credit"],
      ["page.online_sales", "/sales/online"], ["page.cash_sales", "/sales/cash"],
      ["page.petty_cash", "/pettycash"], ["page.company_dashboard", "/company"],
      ["page.attendance", "/attendance"], ["page.kpi", "/kpi"],
      ["page.stock_control", "/stock"], ["page.payment_tracking", "/payments"], ["page.branch_master", "/settings/branches"],
      ["page.users_access", "/settings/users"], ["page.audit_log", "/settings/audit"],
    ].find(([key]) => can(key));
    if (firstAllowed && firstAllowed[1] !== window.location.pathname) return <Navigate to={firstAllowed[1]} replace />;
    return <Layout><div className="bg-white border border-slate-200 rounded-sm p-8"><h1 className="text-xl font-semibold">Access not assigned</h1><p className="text-sm text-slate-500 mt-2">Ask an administrator to enable a page in Users & Access.</p></div></Layout>;
  }
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<AcceptInvitation />} />
      <Route path="/" element={<Protected permission="page.overview"><SalesPerformance /></Protected>} />
      <Route path="/sales/credit" element={<Protected permission="page.credit_sales"><CreditSales /></Protected>} />
      <Route path="/sales/online" element={<Protected permission="page.online_sales"><OnlineSales /></Protected>} />
      <Route path="/sales/cash" element={<Protected permission="page.cash_sales"><CashSales /></Protected>} />
      <Route path="/pettycash" element={<Protected permission="page.petty_cash"><PettyCash /></Protected>} />
      <Route path="/company" element={<Protected permission="page.company_dashboard"><CompanyDashboard /></Protected>} />
      <Route path="/attendance" element={<Protected permission="page.attendance"><Attendance /></Protected>} />
      <Route path="/kpi" element={<Protected permission="page.kpi"><KpiManagement /></Protected>} />
      <Route path="/stock" element={<Protected permission="page.stock_control"><StockControl /></Protected>} />
      <Route path="/payments" element={<Protected permission="page.payment_tracking"><PaymentTracking /></Protected>} />
      <Route path="/settings/branches" element={<Protected permission="page.branch_master"><BranchMaster /></Protected>} />
      <Route path="/settings/users" element={<Protected permission="page.users_access"><UsersPage /></Protected>} />
      <Route path="/settings/audit" element={<Protected permission="page.audit_log"><AuditLog /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function VersionWatcher() {
  useEffect(() => {
    let reloading = false;
    const currentAsset = () => [...document.scripts].map((script) => script.src)
      .find((src) => /\/static\/js\/main\.[^/]+\.js/.test(src));
    const check = async () => {
      if (reloading || document.visibilityState === "hidden") return;
      try {
        const html = await fetch(`/?version-check=${Date.now()}`, { cache: "no-store" }).then((response) => response.text());
        const match = html.match(/\/static\/js\/main\.[^"']+\.js/);
        const current = currentAsset();
        if (match && current && !current.includes(match[0])) {
          const reloadKey = `plsc-auto-reload:${match[0]}`;
          if (sessionStorage.getItem(reloadKey)) return;
          sessionStorage.setItem(reloadKey, "1");
          reloading = true;
          window.location.reload();
        }
      } catch (error) { /* retry on the next visibility check */ }
    };
    const timer = window.setInterval(check, 5 * 60 * 1000);
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", check); };
  }, []);
  return null;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <VersionWatcher />
        <DateRangeProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </DateRangeProvider>
      </AuthProvider>
    </div>
  );
}
